import React, { useState } from 'react'
import type { ComponentType, SVGProps } from 'react'
import { Link, useHistory, useLocation } from 'react-router-dom'
import { FontIcon, ListItem } from '@arvoreeducacao/bonsai-ds'
import type { IconsType } from '@arvoreeducacao/bonsai-core'
import { useTranslation } from 'i18n'
import ClickOutSide from 'utils/ClickOutSide'
import { cn } from 'utils/cn'
import { getImageUrl } from 'utils/assets/Cdn'
import { useUserInfo } from 'utils/hooks/use-userInfo'
import useFeatureToggles from 'utils/hooks/use-feature-toggles'
import { openAccessibilityMenu } from 'utils/user-way-accessibility-widget'
import {
  ADMIN_READING_REPORT_PATH,
  BOOK_DETAILS_PATH_ROOT,
  EXPLORE_PATH,
  FOREST_PATH,
  FOREST_TREES_PATH,
  LEAGUE_PATH,
  LOGOUT_PATH,
  MY_ACCESS_PATH,
  SEARCH_PATH_ROOT,
  STORY_TREE_PATH
} from 'routes/paths'
import {
  STUDENT_TUTORIAL_URL,
  TEACHER_TUTORIAL_URL
} from 'components/header-modular/header-desktop/contants'

import { useStoryTreeGamification } from './useStoryTreeGamification'
import { CoinIcon, DropIcon, LeafFilledIcon, LeafIcon, StarIcon } from './icons'

const APP_V2_ORIGIN = ''

const STORY_TREE_LOGO_URL = getImageUrl('story-tree/story-tree-logo.svg')
const ACTIVE_COLOR = '#45D0C1'

type SvgIcon = ComponentType<SVGProps<SVGSVGElement>>

const NAV_ITEMS: Array<{
  label: string
  href: string
  matchPaths: string[]
  iconName: IconsType
  iconNameFilled: IconsType
  customIcon?: SvgIcon
  customIconFilled?: SvgIcon
}> = [
  {
    label: 'Home',
    href: `${APP_V2_ORIGIN}/app-v2/story-tree`,
    matchPaths: [STORY_TREE_PATH],
    iconName: 'home',
    iconNameFilled: 'home-filled'
  },
  {
    label: 'Journeys',
    href: `${APP_V2_ORIGIN}/app-v2/journeys`,
    matchPaths: [],
    iconName: 'leaf-outline',
    iconNameFilled: 'leaf',
    customIcon: LeafIcon,
    customIconFilled: LeafFilledIcon
  },
  {
    label: 'Library',
    href: `${APP_V2_ORIGIN}/app-v2/story-tree/library`,
    matchPaths: [SEARCH_PATH_ROOT, EXPLORE_PATH, BOOK_DETAILS_PATH_ROOT],
    iconName: 'book-open',
    iconNameFilled: 'book-open'
  },
  {
    label: 'Rewards',
    href: `${APP_V2_ORIGIN}/biblioteca/floresta`,
    matchPaths: [FOREST_PATH, FOREST_TREES_PATH, LEAGUE_PATH],
    iconName: 'medal',
    iconNameFilled: 'medal-filled'
  },
  {
    label: 'My Readings',
    href: `${APP_V2_ORIGIN}/app-v2/my-readings`,
    matchPaths: ['/biblioteca/minhas-leituras'],
    iconName: 'book-person',
    iconNameFilled: 'book-person-filled'
  },
  {
    label: 'Assignments',
    href: `${APP_V2_ORIGIN}/app-v2/activities`,
    matchPaths: [],
    iconName: 'clipboard-content',
    iconNameFilled: 'clipboard-content-filled'
  }
]

type StoryTreeNavItem = typeof NAV_ITEMS[number]

const TEACHER_NAV_ITEMS: StoryTreeNavItem[] = [
  {
    label: 'Educator Dashboard',
    href: `${APP_V2_ORIGIN}/app-v2/admin/class-journey-report`,
    matchPaths: [ADMIN_READING_REPORT_PATH],
    iconName: 'graduation-cap',
    iconNameFilled: 'graduation-cap-filled'
  },
  NAV_ITEMS.find(item => item.label === 'Library') as StoryTreeNavItem,
  NAV_ITEMS.find(item => item.label === 'My Readings') as StoryTreeNavItem
]

export function StoryTreeHeader() {
  const location = useLocation()
  const history = useHistory()
  const { t } = useTranslation()
  const { user } = useUserInfo()
  const { featureToggles } = useFeatureToggles()
  const { coins, drops, lexileScore } = useStoryTreeGamification()
  const [profileOpen, setProfileOpen] = useState(false)

  const firstName = user?.name?.split(' ')[0] ?? ''
  const isTeacher = Boolean(user?.teacher?.id)
  const navItems = isTeacher ? TEACHER_NAV_ITEMS : NAV_ITEMS

  const handleAccessibility = () => {
    openAccessibilityMenu(featureToggles?.vLibrasEnabled)
    setProfileOpen(false)
  }

  const getHelpLink = () =>
    isTeacher ? TEACHER_TUTORIAL_URL : STUDENT_TUTORIAL_URL

  const isActive = (matchPaths: string[]) => {
    const path = location.pathname
    return matchPaths.some(
      match => path === match || path.startsWith(`${match}/`)
    )
  }

  return (
    <header className="sticky top-0 z-50 w-full bg-white shadow-[0px_4px_2.5px_rgba(5,59,75,0.06)]">
      <div className="flex h-14 items-center justify-between px-4 sm:h-[70px] sm:px-6">
        <a
          className="flex shrink-0 items-center"
          href={
            isTeacher
              ? `${APP_V2_ORIGIN}/app-v2/admin/class-journey-report`
              : STORY_TREE_PATH
          }
        >
          <img
            alt="StoryTree"
            className="h-6 w-auto sm:h-[30px]"
            height={30}
            src={STORY_TREE_LOGO_URL}
          />
        </a>

        <div
          className={cn(
            'hidden h-full items-center gap-2',
            isTeacher ? 'sm:flex' : 'xl:flex'
          )}
        >
          {navItems.map(item => {
            const active = isActive(item.matchPaths)
            const iconName = active ? item.iconNameFilled : item.iconName
            const CustomIcon = active ? item.customIconFilled : item.customIcon
            return (
              <a
                className="relative flex h-full items-center"
                href={item.href}
                key={item.label}
              >
                <div
                  className={cn(
                    'flex items-center gap-3 rounded-large px-4 py-4 transition-colors',
                    !active && 'hover:bg-gray-100'
                  )}
                >
                  {CustomIcon ? (
                    <CustomIcon
                      className="size-6"
                      style={{
                        color: active ? ACTIVE_COLOR : 'var(--color-gray-700)'
                      }}
                    />
                  ) : (
                    <FontIcon
                      color={active ? ACTIVE_COLOR : 'var(--color-gray-700)'}
                      iconName={iconName}
                      size={24}
                    />
                  )}
                  <span
                    className={cn(
                      'text-sm font-bold',
                      active ? 'text-[#45D0C1]' : 'text-gray-700'
                    )}
                  >
                    {item.label}
                  </span>
                </div>
                {active && (
                  <div className="absolute inset-x-0 bottom-0 h-[3px] rounded-t bg-[#45D0C1]" />
                )}
              </a>
            )
          })}
        </div>

        <div className="flex items-center gap-6">
          {!isTeacher && (
            <div className="hidden items-center gap-2 sm:flex xl:hidden">
              <div className="flex items-center gap-1 rounded-full bg-[#FFFBEE] py-2 pl-2 pr-4">
                <CoinIcon className="size-[26px]" />
                <span className="text-xl font-bold leading-tight text-gray-700">
                  {coins}
                </span>
              </div>
              <div className="flex items-center gap-1 rounded-full bg-[#EEE4F7] py-2 pl-2 pr-4">
                <StarIcon className="size-[26px]" />
                <span className="text-xl font-bold leading-tight text-gray-700">
                  {lexileScore}
                </span>
              </div>
              <div className="flex items-center gap-1 rounded-full bg-[#EBF9FF] py-2 pl-2 pr-4">
                <DropIcon className="size-[26px]" />
                <span className="text-xl font-bold leading-tight text-gray-700">
                  {drops}
                </span>
              </div>
            </div>
          )}

          <Link
            className="flex size-8 items-center justify-center rounded-full bg-gray-300 sm:hidden"
            to={MY_ACCESS_PATH}
          >
            <FontIcon color="var(--color-white)" iconName="user" size={16} />
          </Link>

          <div className="relative hidden sm:block">
            <button
              className={cn(
                'flex items-center gap-2 rounded-large px-3 py-4 transition-colors hover:bg-gray-100',
                profileOpen && 'bg-gray-100'
              )}
              onClick={() => setProfileOpen(!profileOpen)}
              type="button"
            >
              <span className="text-base font-bold text-gray-900">
                {firstName}
              </span>
              <FontIcon
                color="var(--color-gray-900)"
                iconName="caret-down"
                size={16}
              />
            </button>

            {profileOpen && (
              <div className="absolute right-0 top-full z-50 mt-2 w-64 rounded-large bg-white py-2 shadow-[0px_4px_12px_rgba(5,59,75,0.12)]">
                <ClickOutSide onClickOutSide={() => setProfileOpen(false)}>
                  <ListItem
                    icon="padlock"
                    text={t('Meu Acesso')}
                    onClick={() => {
                      setProfileOpen(false)
                      history.push(MY_ACCESS_PATH)
                    }}
                  />

                  <ListItem
                    icon="universal-access"
                    text={t('Acessibilidade')}
                    onClick={handleAccessibility}
                  />

                  <ListItem
                    icon="headset"
                    text={t('Precisa de ajuda?')}
                    onClick={() => {
                      setProfileOpen(false)
                      window.open(getHelpLink(), '_blank')
                    }}
                  />

                  <ListItem
                    icon="information"
                    text={t('Central Árvore')}
                    onClick={() => {
                      setProfileOpen(false)
                      window.location.href = 'https://central.arvore.com.br'
                    }}
                  />

                  <ListItem
                    icon="magazine"
                    text={t('Sair')}
                    onClick={() => history.push(LOGOUT_PATH)}
                  />
                </ClickOutSide>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  )
}

export default StoryTreeHeader
